import { useMemo, useState } from "react";
import classNames from "classnames";
import { ArrowIcon } from "../../assets/icons";
import { addMonths } from "../../utils/addMonths";
import { CalendarDay } from "../calendar/calendarDay";
import calendarStyles from "../calendar/calendar.module.css";
import styles from "./periodRangeCalendar.module.css";
import { Text } from "../text/text";

const weekDays = ["ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "ВС"];

const MONTH_NAMES = [
    "январь", "февраль", "март", "апрель", "май", "июнь",
    "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь",
];

const monthKey = (year, month) => year * 12 + month;

const formatMonthYear = (month, year) => {
    const name = MONTH_NAMES[month - 1] || "";
    return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`;
};

const getDayType = (date, startKey, endKey, isSecondary) => {
    if (isSecondary) return "secondary";
    const key = monthKey(date.getFullYear(), date.getMonth() + 1);
    if (startKey == null || endKey == null) return "primary";
    if (key < startKey || key > endKey) return "primary";
    if (key === startKey || key === endKey) return "info";
    return "range";
};

export const PeriodRangeCalendar = ({
    startMonth,
    startYear,
    endMonth,
    endYear,
    onChange,
    error = false,
}) => {
    const [viewDate, setViewDate] = useState(
        () => new Date(endYear, endMonth - 1, 1),
    );

    const startKey = monthKey(startYear, startMonth);
    const endKey = monthKey(endYear, endMonth);
    const monthsCount = endKey >= startKey ? endKey - startKey + 1 : 0;

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const monthName = viewDate.toLocaleString("ru-RU", { month: "long" });

    const { prevMonth, curMonth, nextMonth } = useMemo(() => {
        const prev = [];
        const lastDayOfPrevMonth = new Date(year, month, 0).getDate();
        const weekIdOfFirstDay = new Date(year, month, 1).getDay();
        const cntOfPrevDayz = weekIdOfFirstDay || 7;
        for (let i = 0; i < cntOfPrevDayz - 1; i++) {
            prev.push(lastDayOfPrevMonth - i);
        }
        prev.reverse();

        const cur = [];
        const cntOfDays = 32 - new Date(year, month, 32).getDate();
        for (let i = 1; i <= cntOfDays; i++) {
            cur.push(i);
        }

        const next = [];
        for (let i = 0; i < (7 - (cntOfDays + cntOfPrevDayz - 1) % 7) % 7; i++) {
            next.push(i + 1);
        }
        return { prevMonth: prev, curMonth: cur, nextMonth: next };
    }, [year, month]);

    const handleDayClick = (clickedDate) => {
        const clickedMonth = clickedDate.getMonth() + 1;
        const clickedYear = clickedDate.getFullYear();
        const clickedKey = monthKey(clickedYear, clickedMonth);

        if (startKey === endKey) {
            if (clickedKey >= startKey) {
                onChange({
                    startMonth,
                    startYear,
                    endMonth: clickedMonth,
                    endYear: clickedYear,
                });
            } else {
                onChange({
                    startMonth: clickedMonth,
                    startYear: clickedYear,
                    endMonth: clickedMonth,
                    endYear: clickedYear,
                });
            }
            return;
        }

        onChange({
            startMonth: clickedMonth,
            startYear: clickedYear,
            endMonth: clickedMonth,
            endYear: clickedYear,
        });
    };

    return (
        <div className={classNames(styles.wrapper, { [styles.error]: error })}>
            <div className={styles.summary}>
                <div className={styles.summaryItem}>
                    <Text as="span" variant="caption" className={styles.summaryLabel}>
                        С
                    </Text>
                    <Text as="span" variant="bodyStrong" className={styles.summaryValue}>
                        {formatMonthYear(startMonth, startYear)}
                    </Text>
                </div>
                <Text as="span" variant="body" className={styles.summaryDivider}>
                    —
                </Text>
                <div className={styles.summaryItem}>
                    <Text as="span" variant="caption" className={styles.summaryLabel}>
                        По
                    </Text>
                    <Text as="span" variant="bodyStrong" className={styles.summaryValue}>
                        {formatMonthYear(endMonth, endYear)}
                    </Text>
                </div>
            </div>

            {monthsCount > 0 && (
                <Text as="div" variant="caption" className={styles.monthsHint}>
                    {monthsCount}{" "}
                    {monthsCount === 1
                        ? "месяц"
                        : monthsCount < 5
                          ? "месяца"
                          : "месяцев"}{" "}
                    в отчёте
                </Text>
            )}

            <Text as="div" variant="caption" className={styles.tapHint}>
                Нажмите день в календаре: первый раз — начало периода, второй — конец
            </Text>

            <div className={classNames(calendarStyles.calendar, styles.calendar)}>
                <div className={calendarStyles.calendar_header}>
                    <ArrowIcon
                        className={calendarStyles.calendar_arrow}
                        angle={180}
                        onClick={() => setViewDate(addMonths(viewDate, -1))}
                    />
                    <Text as="div" variant="h3" className={calendarStyles.month_name}>
                        {monthName} {year}
                    </Text>
                    <ArrowIcon
                        className={calendarStyles.calendar_arrow}
                        onClick={() => setViewDate(addMonths(viewDate, 1))}
                    />
                </div>
                <div className={calendarStyles.calendar_data}>
                    {weekDays.map((day, i) => (
                        <Text
                            as="div"
                            variant="body"
                            className={calendarStyles.calendar_week_day}
                            key={i}
                        >
                            {day}
                        </Text>
                    ))}

                    {prevMonth.map((day, i) => {
                        const date = new Date(year, month - 1, day, 12);
                        return (
                            <CalendarDay
                                key={`p-${i}`}
                                date={date}
                                type={getDayType(date, startKey, endKey, true)}
                                onClick={() => handleDayClick(date)}
                            />
                        );
                    })}

                    {curMonth.map((day, i) => {
                        const date = new Date(year, month, day, 12);
                        return (
                            <CalendarDay
                                key={`c-${i}`}
                                date={date}
                                type={getDayType(date, startKey, endKey, false)}
                                onClick={() => handleDayClick(date)}
                            />
                        );
                    })}

                    {nextMonth.map((day, i) => {
                        const date = new Date(year, month + 1, day, 12);
                        return (
                            <CalendarDay
                                key={`n-${i}`}
                                date={date}
                                type={getDayType(date, startKey, endKey, true)}
                                onClick={() => handleDayClick(date)}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
